import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as PUBLIC_API from "../../../../../app/public_api";
import { AxiosError } from "axios";
import {
  CreationsGETResponse,
  CreationYearsStatsGETResponse,
} from "../../../../../utils-creation/types";
import { ResultItem, RootState, statusType } from "../../../../../utils/types";
import { COUNT_PER_PAGE } from "../../../../../utils-creation/constants";

export interface CreationTabState {
  creationsList: ResultItem[];
  status: statusType;
  total: number;
  currentPage: number;
  hasNext: boolean;
  yearFilter: {
    total: number;
    year: number;
    years: { year: number; count: number }[];
    status: statusType;
  };
}

const initialState: CreationTabState = {
  creationsList: [],
  status: "idle",
  total: -1,
  currentPage: 0,
  hasNext: false,
  yearFilter: {
    total: -1,
    year: -1,
    years: [],
    status: "idle",
  },
};

export const initList = createAsyncThunk(
  "profilePage/creationTab/initList",
  async (
    data: {
      randomID: string;
      year: number;
    },
    thunkAPI
  ) => {
    try {
      const { year, randomID } = data;
      const { currentPage } = (thunkAPI.getState() as RootState)
        .profilePg_creationTab;
      return await PUBLIC_API.getCreationsList({
        randomID,
        year,
        page: currentPage + 1,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const loadMore = createAsyncThunk(
  "profilePage/creationTab/loadMore",
  async (
    data: {
      randomID: string;
      year: number;
    },
    thunkAPI
  ) => {
    try {
      const { year, randomID } = data;
      const { currentPage } = (thunkAPI.getState() as RootState)
        .profilePg_creationTab;
      return await PUBLIC_API.getCreationsList({
        randomID,
        year,
        page: currentPage + 1,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const getCreationYearsStats = createAsyncThunk(
  "profilePage/creationTab/getCreationYearsStats",
  async (randomID: string) => {
    try {
      return await PUBLIC_API.getCreationYearsStats({ randomID });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

const creationTabSlice = createSlice({
  name: "profilePage/creationTab",
  initialState,
  reducers: {
    resetState: () => initialState,
    setYear: (state, action: PayloadAction<number>) => {
      state.yearFilter.year = action.payload;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      // initList
      .addCase(initList.pending, (state) => {
        state.status = "loading";
        state.total = initialState.total;
        state.hasNext = initialState.hasNext;
        state.currentPage = initialState.currentPage;
        state.creationsList = initialState.creationsList;

        // set the list to be COUNT_PER_PAGE skeleton items
        state.creationsList = [...new Array(COUNT_PER_PAGE)].map(() => ({
          loading: true,
          item: null,
        }));
      })
      .addCase(
        initList.fulfilled,
        (state, action: PayloadAction<CreationsGETResponse | unknown>) => {
          const { links, total_result, results } =
            action.payload as CreationsGETResponse;

          state.status = "succeeded";
          state.total = total_result;
          state.hasNext = !!links?.next;
          state.currentPage += 1;

          // set the list to what we received
          state.creationsList = results?.map((x) => ({
            loading: false,
            item: x,
          }));
        }
      )
      .addCase(initList.rejected, (state) => {
        state.status = "failed";
      })
      // loadMore
      .addCase(loadMore.pending, (state) => {
        state.status = "loading";

        // add COUNT_PER_PAGE skeleton items to the list
        state.creationsList = state.creationsList.concat(
          [...new Array(COUNT_PER_PAGE)].map(() => ({
            loading: true,
            item: null,
          }))
        );
      })
      .addCase(
        loadMore.fulfilled,
        (state, action: PayloadAction<CreationsGETResponse | unknown>) => {
          const { links, total_result, results } =
            action.payload as CreationsGETResponse;

          state.status = "succeeded";
          state.total = total_result;
          state.hasNext = !!links.next;
          state.currentPage += 1;

          // replace the last cntPerPage skeleton item list
          // to the list we received
          state.creationsList = state.creationsList
            .slice(0, -COUNT_PER_PAGE)
            .concat(results?.map((x) => ({ loading: false, item: x })));
        }
      )
      .addCase(loadMore.rejected, (state) => {
        state.status = "failed";
      })
      // getCreationYearsStats
      .addCase(getCreationYearsStats.pending, (state) => {
        state.yearFilter.status = "loading";
      })
      .addCase(
        getCreationYearsStats.fulfilled,
        (
          state,
          action: PayloadAction<CreationYearsStatsGETResponse | unknown>
        ) => {
          state.yearFilter.status = "succeeded";

          const { total, years } =
            action.payload as CreationYearsStatsGETResponse;

          state.yearFilter.years = years;
          state.yearFilter.total = total;
        }
      )
      .addCase(getCreationYearsStats.rejected, (state) => {
        state.yearFilter.status = "failed";
      });
  },
});

export const { resetState, setYear } = creationTabSlice.actions;

export default creationTabSlice;
